import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";

import { LanguageProvider } from "@/i18n/LanguageContext";
import Navbar from "@/components/Navbar";
import HeroCarousel from "@/components/HeroCarousel";
import ExpertiseSection from "@/components/ExpertiseSection";
import SoftwareSection from "@/components/SoftwareSection";
import NewsSection from "@/components/NewsSection";
import ContactSection from "@/components/ContactSection";
import Footer from "@/components/Footer";
import AdminLogin from "@/components/AdminLogin";
import AdminPanel from "@/components/AdminPanel";
import AdminRoute from "@/components/AdminRoute";
import SoftwarePage from "@/components/SoftwarePage";
import LegalPage from "@/components/LegalPage";
import DemoPage from "@/components/DemoPage";
import CookieConsent from "@/components/CookieConsent";
import MaintenancePage from "@/components/MaintenancePage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Google Analytics loader
const loadGA = (gaId) => {
  if (!gaId || document.getElementById("ga-script")) return;
  const s = document.createElement("script");
  s.id = "ga-script";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", gaId);
};

// Track page views internally
const usePageTracking = () => {
  const location = useLocation();
  useEffect(() => {
    axios.post(`${API}/analytics/pageview`, {
      path: location.pathname,
      referrer: document.referrer,
    }).catch(() => {});
    // GA pageview
    if (window.gtag) {
      window.gtag("event", "page_view", { page_path: location.pathname });
    }
  }, [location.pathname]);
};

function PageTracker() {
  usePageTracking();
  return null;
}

function HomePage() {
  const [slides, setSlides] = useState([]);
  const [software, setSoftware] = useState([]);
  const [news, setNews] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminCredentials, setAdminCredentials] = useState(null);
  const [maintenance, setMaintenance] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [carouselRes, softwareRes, newsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/carousel`),
          axios.get(`${API}/software`),
          axios.get(`${API}/news`),
          axios.get(`${API}/settings`),
        ]);
        setSlides(carouselRes.data);
        setSoftware(softwareRes.data);
        setNews(newsRes.data);
        const s = settingsRes.data;
        setSettings(s);
        if (s?.maintenance_mode) setMaintenance(true);
        // Load GA if cookie accepted
        if (s?.ga_id && localStorage.getItem("sciad_cookie_consent") === "accepted") {
          loadGA(s.ga_id);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const handleCookieAccept = () => {
    if (settings?.ga_id) loadGA(settings.ga_id);
  };

  const handleAdminLogin = (credentials) => {
    setAdminCredentials(credentials);
    setShowAdminLogin(false);
  };

  const handleAdminLogout = () => {
    setAdminCredentials(null);
    window.location.reload();
  };

  if (adminCredentials) {
    return <AdminPanel credentials={adminCredentials} onClose={handleAdminLogout} />;
  }

  if (maintenance) {
    return (
      <>
        <MaintenancePage onAdminLogin={handleAdminLogin} />
        <CookieConsent onAccept={handleCookieAccept} />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1121]">
      <Navbar />
      
      {loading ? (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-[#00CCC2] border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-slate-400">Chargement...</p>
          </div>
        </div>
      ) : (
        <main>
          <HeroCarousel slides={slides} />
          <ExpertiseSection />
          <SoftwareSection software={software} />
          <NewsSection news={news} />
          <ContactSection settings={settings} />
          <Footer onAdminClick={() => setShowAdminLogin(true)} />
        </main>
      )}

      {showAdminLogin && (
        <AdminLogin onLogin={handleAdminLogin} onClose={() => setShowAdminLogin(false)} />
      )}
      <CookieConsent onAccept={handleCookieAccept} />
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <div className="App">
        <BrowserRouter>
          <PageTracker />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/logiciel/:slug" element={<SoftwarePage />} />
            <Route path="/legal/:type" element={<LegalPage />} />
            <Route path="/demo" element={<DemoPage />} />
            <Route path="/admin" element={<AdminRoute />} />
          </Routes>
        </BrowserRouter>
      </div>
    </LanguageProvider>
  );
}

export default App;
